import { graphql, useStaticQuery } from 'gatsby';
import { useState, useEffect } from 'react';

interface UseProductDataProps {
  slug: string;
  pageContext: any;
}

interface UseProductDataResult {
  productId: string | null;
  loading: boolean;
  error: string | null;
  productName: string;
}

/**
 * Custom hook to fetch product data by slug
 * @param slug - The product slug from URL params
 * @param pageContext - The Gatsby page context
 * @returns Object containing productId, loading state, error state, and formatted product name
 */
export const useProductData = ({
  slug,
  pageContext,
}: UseProductDataProps): UseProductDataResult => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Clean the slug for display and API use
  const cleanSlug = (slug || '').replace(/^\/|\/$/g, '');

  // Format product name for display - only use the last part of the path
  const productName =
    cleanSlug
      .split('/')
      .pop()
      ?.replace(/-/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ') || '';

  // Query all product slugs and IDs at build time
  const data = useStaticQuery(graphql`
    query ProductSlugs {
      allContentfulPage001 {
        nodes {
          slug
          hero {
            ... on ContentfulProductDetails {
              id
              productName
            }
          }
        }
      }
    }
  `);

  // Find matching product from static query
  const slugsToTry = [
    pageContext.slug,
    `/products/${cleanSlug}/`,
    `/products/${cleanSlug}`,
    cleanSlug,
    `/${cleanSlug}/`,
    `/${cleanSlug}`,
  ].filter(Boolean);

  const matchingProduct = data.allContentfulPage001.nodes.find((node: any) =>
    slugsToTry.some((slugToTry) => node.slug === slugToTry),
  );

  const productId = matchingProduct?.hero?.id || null;

  useEffect(() => {
    setLoading(false);
    if (!productId) {
      setError(`Product not found for slug: ${cleanSlug}`);
    }
  }, [productId, cleanSlug]);

  return { productId, loading, error, productName };
};

export default useProductData;
